import React, { useCallback, useEffect, useRef, useState, TouchEvent as ReactTouchEvent } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { Button, LinkButton } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { ParagraphLG, ParagraphMD, TitleSM } from '@/atoms/Text'
import { paths } from '@/constants'
import { Avatar } from '@/molecules/Avatar'
import { CatalogTitle } from '@/services/ContentCatalog'
import { SharedContent } from '@/services/ContentShareService'
import { useUser } from '@/services/UserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useTranslate } from '@/utils/translate/translate-client'
import { UserPublicProfile } from '@/utils/users/usersServer'

interface Props {
  showModal: boolean
  handleModalClose: () => void
  moviePosterPath?: string | null
  catalogTitle?: CatalogTitle
  sharedContent?: SharedContent
  sharerPublicProfile?: UserPublicProfile
  contentClaimed?: boolean
  hasUser?: boolean
  loginUrl: string
  sharerLocation?: string
  projectSlug?: string
  fallbackTitle: string
}

export const GuildContentRecipientSheet: React.FC<Props> = ({
  showModal,
  handleModalClose,
  moviePosterPath,
  catalogTitle,
  sharedContent,
  sharerPublicProfile,
  contentClaimed = false,
  hasUser = false,
  loginUrl,
  sharerLocation,
  projectSlug,
  fallbackTitle,
}) => {
  const { t } = useTranslate('watch')
  const { user } = useUser()

  const handleStartWatching = () => {
    handleModalClose()
  }
  const ref = useRef<HTMLDivElement | null>(null)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const minSwipeDistance = 250
  const sharerName = sharerPublicProfile?.firstName ?? t('anAngelGuildMember', 'a Guild Member')

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (showModal && ref.current && !ref.current.contains(event.target as Node)) {
        handleModalClose()
      }
    },
    [ref, handleModalClose, showModal],
  )

  const onTouchStart = (event: ReactTouchEvent) => {
    setTouchEnd(0)
    setTouchStart(event.targetTouches[0].clientY)
  }

  const onTouchMove = (event: ReactTouchEvent) => setTouchEnd(event.targetTouches[0].clientY)

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return
    const html = window.document.getElementsByTagName('html')[0]
    html.style.overflow = 'hidden'
    const distance = touchStart - touchEnd
    const isSwipeDown = distance < -minSwipeDistance
    if (showModal && isSwipeDown) {
      handleModalClose()
      html.style.overflow = 'auto'
    }
  }, [handleModalClose, showModal, touchStart, touchEnd])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [handleClickOutside])

  return (
    <>
      {showModal && (
        <div
          className={classNames(
            'fixed inset-0 z-[1999] transition-all duration-300',
            showModal ? 'bg-black/75 bg-opacity-30' : 'bg-transparent bg-opacity-100',
          )}
        />
      )}
      <div
        ref={ref}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className={classNames(
          'box-border fixed bottom-0 left-0 z-[2000] max-h-[90vh] w-full rounded-t-3xl border-t bg-white transition-all duration-300 ease-in-out text-black',
          'sm:max-w-[400px] sm:right-0 sm:mx-auto sm:rounded-b-3xl sm:overflow-hidden sm:h-fit p-4',
          showModal ? 'translate-y-0 sm:my-auto sm:bottom-0 sm:top-0 sm:opacity-1' : 'translate-y-full sm:opacity-0',
        )}
      >
        <LazyHydrate whenVisible>
          <div className="flex flex-col">
            <div className="">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <TitleSM weight="bold">
                    {contentClaimed
                      ? t('thisInviteLinkHasExpired', 'This invite link has expired or already been watched.')
                      : sharedContent?.recipient_name?.length || user?.profile?.firstName
                      ? t('helloRecipient', 'Hello {{ recipient }}!', {
                          recipient: sharedContent?.recipient_name?.length
                            ? sharedContent?.recipient_name
                            : user?.profile?.firstName,
                        })
                      : t('helloUser', 'Hello!')}
                  </TitleSM>
                  <When condition={!contentClaimed && hasUser}>
                    <div onClick={handleModalClose} className="cursor-pointer">
                      <CloseIcon color="black" size={24} className="cursor-pointer" />
                    </div>
                  </When>
                </div>
                <div className="flex w-full flex-col gap-4 rounded-2xl">
                  <div>
                    {moviePosterPath && (
                      <div className="block max-w-[198px] overflow-hidden rounded-lg">
                        <img
                          src={getCloudinaryImageUrl({ path: moviePosterPath, width: 198, height: 120 })}
                          className="min-h-[120px] min-w-[198px]"
                          alt={t('moviePosterAlt', '{{name}} Movie Poster', { name: catalogTitle?.title })}
                          height={120}
                          width={198}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex w-full flex-col gap-4">
                    <ParagraphLG weight={contentClaimed ? 'bold' : 'medium'}>
                      {contentClaimed ? `${t('thankYouForWatching', 'Thank you for watching')}!` : ''}
                    </ParagraphLG>
                    <ParagraphLG weight="medium">
                      {contentClaimed
                        ? t(
                            'signUpWatchMoreLikeThis',
                            'Sign up for the Guild to watch {{ projectName }} and many more light amplifying shows and movies!',
                            { projectName: catalogTitle?.title },
                          )
                        : sharedContent?.sender_note?.length
                        ? sharedContent.sender_note
                        : fallbackTitle}
                    </ParagraphLG>
                    <When condition={!contentClaimed}>
                      <div className="mt-4 flex flex-row items-center gap-4">
                        <Avatar src={sharerPublicProfile?.image} size={56} />
                        <ParagraphMD weight="medium" color="core-gray-700">
                          {sharerPublicProfile?.firstName} {sharerPublicProfile?.lastName} {sharerLocation}
                        </ParagraphMD>
                      </div>
                      <When condition={hasUser}>
                        <Button
                          variant="core-gray-900"
                          className="mt-4 max-h-[56px] w-fit rounded-2xl px-7 py-[17px]"
                          onClick={handleStartWatching}
                        >
                          <PlayIcon color="white" size={24} className="mr-1.5" />
                          {t('watchFreeThanksTo', 'Watch Free Thanks to {{ sharerName }}', {
                            sharerName,
                          })}
                        </Button>
                      </When>
                      <When condition={!hasUser}>
                        <LinkButton
                          variant="core-gray-900"
                          className="mt-4 max-h-[56px] w-fit rounded-2xl px-7 py-[17px]"
                          href={loginUrl}
                        >
                          {t('signUpWatchFree', 'Sign Up to Watch Free')}
                        </LinkButton>
                      </When>
                    </When>
                    <When condition={contentClaimed}>
                      <div className="flex flex-col gap-1 lg:flex-row">
                        <LinkButton
                          href={`${paths.guild.join}/${projectSlug}`}
                          variant="core-gray-900"
                          className="mt-4 max-h-[56px] w-full gap-1.5 rounded-2xl px-7 py-[17px] lg:w-fit"
                          onClick={handleStartWatching}
                        >
                          <GuildIconMono color="white" size={24} />
                          {t('learnMore', 'Learn More')}
                        </LinkButton>
                        <LinkButton
                          href={paths.watch.index}
                          variant="core-gray-900"
                          className="mt-4 max-h-[56px] w-full gap-1.5 rounded-2xl px-7 py-[17px] lg:w-fit"
                          onClick={handleStartWatching}
                        >
                          {t('browseAngelOriginals', 'Browse Angel Originals')}
                        </LinkButton>
                      </div>
                      <When condition={!hasUser}>
                        <ParagraphLG weight="medium" className="mb-8 mt-4">
                          <a
                            className="mt-4 max-h-[56px] w-fit min-w-[178px] cursor-pointer rounded-2xl py-[17px] underline"
                            href={loginUrl}
                          >
                            {t('alreadyClaimedLogin', 'Already claimed this link? Log in to watch!')}
                          </a>
                        </ParagraphLG>
                      </When>
                    </When>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LazyHydrate>
      </div>
    </>
  )
}
